import { Slider } from '../../components/Slider';
import { HelpTooltip } from '../../components/HelpTooltip';
import { useEditorStore } from '../../store/editorStore';
import {
  defaultMaskAdjustments,
  type Mask,
  type MaskKind,
} from '../editState';

const KIND_HELP = {
  brush: 'brush',
  linear: 'linearGradient',
  radial: 'radialGradient',
} as const;

function newMask(kind: MaskKind, index: number): Mask {
  const base: Mask = {
    id: crypto.randomUUID(),
    kind,
    name: `${kind[0].toUpperCase()}${kind.slice(1)} ${index + 1}`,
    inverted: false,
    adjustments: defaultMaskAdjustments(),
  };
  if (kind === 'linear') base.linear = { x1: 0.5, y1: 0, x2: 0.5, y2: 1 };
  if (kind === 'radial')
    base.radial = { cx: 0.5, cy: 0.5, rx: 0.3, ry: 0.3, feather: 0.5 };
  if (kind === 'brush') base.strokes = [];
  return base;
}

export function MaskingPanel() {
  const masks = useEditorStore((s) => s.state.masks);
  const update = useEditorStore((s) => s.update);

  const add = (kind: MaskKind) =>
    update((d) => d.masks.push(newMask(kind, d.masks.length)));

  const selectedId = masks[masks.length - 1]?.id;
  const selected = masks.find((m) => m.id === selectedId);

  const setAdj = (
    id: string,
    key: keyof Mask['adjustments'],
    value: number,
  ) =>
    update((d) => {
      const m = d.masks.find((x) => x.id === id);
      if (m) m.adjustments[key] = value;
    });

  return (
    <>
      <p className="hint">
        Add mask <HelpTooltip id="masking" />
      </p>
      <div className="rowbtns">
        <button className="chip" onClick={() => add('brush')}>
          + Brush <HelpTooltip id="brush" />
        </button>
        <button className="chip" onClick={() => add('linear')}>
          + Linear <HelpTooltip id="linearGradient" />
        </button>
        <button className="chip" onClick={() => add('radial')}>
          + Radial <HelpTooltip id="radialGradient" />
        </button>
      </div>

      <p className="stub-note">
        AI subject / sky auto-selection is planned. For now, masks store their
        placement &amp; adjustments; on-canvas painting and live mask preview are
        coming next.
      </p>

      {masks.length === 0 && <p className="hint">No masks yet.</p>}

      {masks.map((m) => (
        <div
          key={m.id}
          className={`masklist ${m.id === selectedId ? 'is-active' : ''}`}
        >
          <span className="masklist__name">
            {m.name}
            <HelpTooltip id={KIND_HELP[m.kind]} />
          </span>
          <button
            className="masklist__x"
            aria-label="Delete mask"
            onClick={() =>
              update((d) => {
                d.masks = d.masks.filter((x) => x.id !== m.id);
              })
            }
          >
            ×
          </button>
        </div>
      ))}

      {selected && (
        <div className="maskadj">
          <p className="hint">{selected.name} adjustments</p>
          <label className="checkline">
            <input
              type="checkbox"
              checked={selected.inverted}
              onChange={(e) =>
                update((d) => {
                  const m = d.masks.find((x) => x.id === selected.id);
                  if (m) m.inverted = e.target.checked;
                })
              }
            />
            Invert mask
          </label>
          {(
            [
              ['exposure', 'Exposure'],
              ['contrast', 'Contrast'],
              ['highlights', 'Highlights'],
              ['shadows', 'Shadows'],
              ['temp', 'Temp'],
              ['tint', 'Tint'],
              ['saturation', 'Saturation'],
              ['clarity', 'Clarity'],
              ['sharpness', 'Sharpness'],
            ] as [keyof Mask['adjustments'], string][]
          ).map(([key, label]) => (
            <Slider
              key={key}
              label={label}
              value={selected.adjustments[key]}
              min={-100}
              max={100}
              onChange={(v) => setAdj(selected.id, key, v)}
            />
          ))}
        </div>
      )}
    </>
  );
}
